import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Activity,
  AlertCircle,
  FileSearch,
  Lock,
  Radar,
  RefreshCw,
  RotateCcw,
  Save,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import PorteurNotification from './ui/PorteurNotification';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Encadre, Interrupteur, LigneReglage } from './parametrage/PiecesReglage';
import { useNotification } from '../hooks/useNotification';
import { formaterFCFA } from '../utils/montants';
import { jouerSon } from '../utils/sonsKlasys';
import {
  lireReglagesAlertes,
  enregistrerReglagesAlertes,
  memoriserReglagesSon
} from './alertesCritiques/apiAlertesCritiques';
import SuiviAlertesCritiques, {
  ACCENT_SUIVI,
  BOUTON_REESSAYER,
  useSuiviAlertes
} from './alertesCritiques/SuiviAlertesCritiques';
import DonneesSuspectes, {
  CATEGORIE_SUSPECTES,
  useDonneesSuspectes
} from './alertesCritiques/DonneesSuspectes';

/**
 * Paramètres des alertes critiques (Outils administratifs > Alertes critiques).
 *
 * ----------------------------------------------------------------------------
 * Pourquoi cette page
 * ----------------------------------------------------------------------------
 * Constat du 12/10/2026 à csap1 : 18 élèves inscrits avec une réduction de
 * 999, 998, 497 ou 1 748 au lieu de 1 000, 500 ou 1 750, un cran de molette
 * à chaque fois, d'où des mensualités de 8 001 ou 10 002. La garde contre la
 * molette (utils/gardeMolette.js) empêche la faute de se reproduire ; le
 * panneau des alertes critiques retrouve celles qui sont déjà en base et
 * relance les agents jusqu'à ce qu'elles soient corrigées.
 *
 * Cette page règle le panneau pour toute l'école, et en suit l'effet. Quatre
 * onglets (PARAMETRAGE-MODULES.md §2), depuis le 16/10/2026 :
 *   · Suivi             : l'état des alertes, leur avancement sur 30 jours et
 *                         les relances des agents (lecture seule, demande de
 *                         Daniel du 13/10/2026 ; alertesCritiques/
 *                         SuiviAlertesCritiques.jsx) ;
 *   · Données suspectes : ce qui mérite d'être vérifié avec l'école sans être
 *                         une faute certaine, et l'interrupteur de chaque
 *                         signal, éteint par défaut (GO de Daniel du
 *                         16/10/2026 ; alertesCritiques/DonneesSuspectes.jsx).
 *                         Son ouverture n'écrit rien ;
 *   · Réglages          : rappel, sons et seuils de la faute de molette,
 *                         réunis sous une seule barre Réinitialiser /
 *                         Enregistrer (régime B, brouillon) ;
 *   · Détecteurs        : un interrupteur par détecteur que la licence ouvre
 *                         (régime A, écriture immédiate), données suspectes
 *                         exclues : elles ont leur onglet.
 *
 * ----------------------------------------------------------------------------
 * Une seule écriture à la fois, sur une base relue
 * ----------------------------------------------------------------------------
 * Chaque enregistrement envoie l'objet de réglages COMPLET. Chaque section n'y
 * change donc que les champs que l'agent a modifiés dans son onglet, jamais
 * ceux d'un autre, sur une base RELUE juste avant l'envoi : un autre agent, ou le même
 * dans un autre onglet du navigateur, a pu rallumer un détecteur depuis le
 * chargement, et une base périmée le remettrait à son ancienne valeur sans
 * rien dire. Si la relecture échoue, l'écriture part de la base connue.
 * Les écritures de la page passent l'une après l'autre (file `fileEcritures`) :
 * une bascule de détecteur lancée pendant l'enregistrement des seuils part de
 * l'état que le serveur vient de rendre, et ne l'écrase pas.
 *
 * Un interrupteur qui naît éteint (`actif_par_defaut: false`, les données
 * suspectes) part toujours avec une valeur explicite, false s'il n'est pas
 * allumé : la page ne l'allume jamais faute de valeur (completer). Les cases
 * « à traiter » des signaux (`a_traiter`) partent telles que relues.
 *
 * Les brouillons du rappel et des seuils vivent dans la page, pas dans
 * l'onglet : Radix démonte l'onglet qu'on quitte, et un changement d'onglet ne
 * doit pas jeter une saisie non enregistrée. Un point ambre sur l'onglet
 * Réglages la signale. Quand la base change, un champ que l'agent n'a pas
 * touché suit la nouvelle valeur ; un champ en cours de saisie reste tel quel.
 *
 * Le suivi est lu par la page, dès l'ouverture et en même temps que les
 * réglages ; il reste en mémoire quand on change d'onglet. Seuls les
 * détecteurs, les signaux et les seuils changent ce qu'il montre (le rappel et
 * les sons, non) : un tel enregistrement le relit aussitôt si le Suivi est à
 * l'écran (l'agent a pu y passer pendant l'envoi), sinon au retour sur son
 * onglet. Les données suspectes sont lues à la première ouverture de leur
 * onglet, puis gardées de même. Suivi et données suspectes sont réservés aux
 * agents qui peuvent modifier ces réglages, la même porte que
 * `droits.modifier` : sans elle, la page s'ouvre sur les Réglages. Les
 * bandeaux propres aux réglages (migration, droit, année clôturée) ne
 * s'affichent que sur les onglets de réglages : le suivi a les siens.
 *
 * Lecture seule si la migration manque (valeurs par défaut, comme le serveur),
 * si le serveur dit que l'agent ne peut pas modifier (`droits.modifier`), ou
 * si la session est ouverte sur une année clôturée : le verrou des archives
 * refuse alors toute écriture en 423, ces réglages d'école compris. Le drapeau
 * posé à la connexion le dit d'avance ; un 423 inattendu fait de même.
 * Sans droit de lecture (403), la page le dit sans parler de panne ; une
 * réponse incomplète du serveur est traitée comme un échec de lecture, jamais
 * complétée par des valeurs par défaut présentées comme l'état réel.
 */

// Les accents des onglets : violet (Suivi), framboise (Données suspectes, voir
// DonneesSuspectes.jsx), bleu principal (Réglages), teal (Détecteurs).
const ACCENT_REGLAGES = '#246990';
const ACCENT_REGLAGES_SOMBRE = '#1a4f6e';
const ACCENT_DETECTEURS = '#319190';
const ACCENT_DETECTEURS_SOMBRE = '#2a7a79';

// Mêmes valeurs par défaut que le serveur, y compris quand la table manque.
const DEFAUTS = {
  intervalle_minutes: 30,
  son_active: true,
  son_notifications: true,
  arrondi_unite: 50,
  arrondi_tolerance: 5,
  detecteurs: {},
  a_traiter: {}
};

const INTERVALLE_MIN = 5;
const INTERVALLE_MAX = 480;
const UNITES = [25, 50, 100];
// De 1 à 6 comme le serveur ; 5 par défaut, pour la raison donnée dans
// l'encadré « Pourquoi 50 et 5 ? ».
const TOLERANCES = [1, 2, 3, 4, 5, 6];

// « À compléter » (élèves, personnel sans photo) et « À surveiller » (classes
// presque pleines) : gris neutre, jamais ambre ni rouge, comme dans le
// panneau. Plus appuyé que le badge d'un détecteur éteint (gray-50, texte
// gray-400), pour qu'on les distingue.
const GRAVITES = {
  critique: { libelle: 'Critique', classes: 'border-red-200 bg-red-50 text-red-700' },
  importante: { libelle: 'Importante', classes: 'border-amber-200 bg-amber-50 text-amber-700' },
  campagne: { libelle: 'À compléter', classes: 'border-gray-300 bg-gray-100 text-gray-700' },
  surveillance: { libelle: 'À surveiller', classes: 'border-gray-300 bg-gray-100 text-gray-700' }
};

// Les onglets : une classe écrite en entier par accent (Tailwind ne lit que
// des classes complètes). Libellés en entier : sous 640 px, la barre passe sur
// deux rangées de deux, et « Données suspectes » y tient.
const ONGLET = 'rounded-lg text-sm font-semibold text-gray-600 transition-all data-[state=active]:text-white data-[state=active]:shadow-sm';

// ============================================================================
// Lecture et préparation des réglages
// ============================================================================

const booleen = (valeur, defaut) => {
  if (valeur === undefined || valeur === null) return defaut;
  return !(valeur === false || valeur === 0 || valeur === '0');
};

const entier = (valeur, defaut) => {
  // Number(null) et Number('') valent 0 : une valeur absente garde le défaut.
  if (valeur === undefined || valeur === null || valeur === '') return defaut;
  const n = Number(valeur);
  return Number.isInteger(n) ? n : defaut;
};

const estObjet = (valeur) => Boolean(valeur) && typeof valeur === 'object' && !Array.isArray(valeur);

// Session ouverte sur une année clôturée : le serveur refuse toute écriture
// sous /api en 423 (readOnlySchoolYearGuard), ces réglages compris, bien
// qu'ils valent pour l'école et non pour une année. La connexion pose ce
// drapeau, comme le lit AdministrationLayout pour son badge « Lecture seule ».
const MESSAGE_ANNEE_CLOTUREE = "Année clôturée : ces réglages se modifient depuis une session ouverte sur l'année en cours.";

const anneeEnLectureSeule = () => {
  try {
    return localStorage.getItem('annee_scolaire_read_only') === 'true';
  } catch {
    return false;
  }
};

const refusAnneeCloturee = (e) => e?.statut === 423 || e?.code === 'ANNEE_SCOLAIRE_LECTURE_SEULE';

/**
 * La lecture n'est retenue que si elle porte les réglages, leurs détecteurs et
 * la liste des détecteurs. Sinon, la page affiche un échec : jamais des
 * valeurs par défaut prises pour l'état réel (PARAMETRAGE-MODULES.md §6).
 */
const reponseComplete = (reponse) => (
  estObjet(reponse?.reglages)
  && estObjet(reponse.reglages.detecteurs)
  && Array.isArray(reponse?.detecteurs)
);

/**
 * Les réglages du serveur ; un champ absent ou illisible prend la valeur de
 * `base` (les valeurs par défaut, ou ce qui vient d'être envoyé). Un serveur
 * d'avant le 16/10/2026 ne rend pas `a_traiter` : il reste vide.
 */
const normaliser = (r, base = DEFAUTS) => {
  const source = r || {};
  const brut = estObjet(source.detecteurs) ? source.detecteurs : (base.detecteurs || {});
  const detecteurs = {};
  Object.entries(brut).forEach(([id, actif]) => { detecteurs[id] = booleen(actif, true); });
  const brutATraiter = estObjet(source.a_traiter) ? source.a_traiter : (base.a_traiter || {});
  const aTraiter = {};
  Object.entries(brutATraiter).forEach(([id, coche]) => { aTraiter[id] = coche === true; });
  return {
    intervalle_minutes: entier(source.intervalle_minutes, base.intervalle_minutes),
    son_active: booleen(source.son_active, base.son_active),
    son_notifications: booleen(source.son_notifications, base.son_notifications),
    arrondi_unite: entier(source.arrondi_unite, base.arrondi_unite),
    arrondi_tolerance: entier(source.arrondi_tolerance, base.arrondi_tolerance),
    detecteurs,
    a_traiter: aTraiter
  };
};

/**
 * L'objet complet envoyé au serveur. Chaque détecteur affiché y figure avec
 * une valeur explicite ; ceux que la licence ferme gardent la leur, pour
 * revenir tels quels si la licence revient. Un détecteur qui naît éteint
 * (`actif_par_defaut: false`) n'est allumé que par un true explicite.
 */
const completer = (r, listeDetecteurs) => {
  const detecteurs = { ...(r.detecteurs || {}) };
  listeDetecteurs.forEach((d) => {
    detecteurs[d.id] = d.actif_par_defaut === false ? detecteurs[d.id] === true : detecteurs[d.id] !== false;
  });
  return {
    intervalle_minutes: r.intervalle_minutes,
    son_active: r.son_active,
    son_notifications: r.son_notifications,
    arrondi_unite: r.arrondi_unite,
    arrondi_tolerance: r.arrondi_tolerance,
    detecteurs,
    a_traiter: { ...(r.a_traiter || {}) }
  };
};

const versBrouillonRappel = (r) => ({
  intervalle: String(r.intervalle_minutes),
  son_active: r.son_active,
  son_notifications: r.son_notifications
});

const versBrouillonSeuils = (r) => ({
  arrondi_unite: r.arrondi_unite,
  arrondi_tolerance: r.arrondi_tolerance
});

const rappelModifie = (brouillon, r) => Boolean(brouillon && r) && (
  brouillon.intervalle !== String(r.intervalle_minutes)
  || brouillon.son_active !== r.son_active
  || brouillon.son_notifications !== r.son_notifications
);

const seuilsModifies = (brouillon, r) => Boolean(brouillon && r) && (
  brouillon.arrondi_unite !== r.arrondi_unite
  || brouillon.arrondi_tolerance !== r.arrondi_tolerance
);

/**
 * Un brouillon sur une base qui change : chaque champ resté à l'ancienne
 * valeur enregistrée prend la nouvelle ; un champ que l'agent a modifié est
 * gardé.
 */
const rebaser = (brouillon, ancien, nouveau) => {
  if (!brouillon) return brouillon;
  const suivant = { ...brouillon };
  Object.keys(nouveau).forEach((champ) => {
    if (brouillon[champ] === ancien[champ]) suivant[champ] = nouveau[champ];
  });
  return suivant;
};

/** Les champs du brouillon que l'agent a changés par rapport à la base qu'il voyait. */
const champsChanges = (brouillon, vu) => Object.fromEntries(
  Object.entries(brouillon).filter(([champ, valeur]) => valeur !== vu[champ])
);

// ============================================================================
// Validation avant envoi : le message nomme le champ fautif
// ============================================================================

const erreurIntervalle = (texte) => {
  const valeur = String(texte || '').trim();
  if (!/^\d+$/.test(valeur)) {
    return `« Intervalle de réouverture » : indiquez un nombre entier de minutes, entre ${INTERVALLE_MIN} et ${INTERVALLE_MAX}.`;
  }
  const n = Number(valeur);
  if (n < INTERVALLE_MIN || n > INTERVALLE_MAX) {
    return `« Intervalle de réouverture » : ${n} minutes est hors limites, choisissez entre ${INTERVALLE_MIN} et ${INTERVALLE_MAX}.`;
  }
  return null;
};

const erreurSeuils = ({ arrondi_unite: unite, arrondi_tolerance: tolerance }) => {
  if (!UNITES.includes(unite)) return '« Unité d\'arrondi » : choisissez 25, 50 ou 100 FCFA.';
  if (!Number.isInteger(tolerance) || tolerance < 1 || tolerance > 6) {
    return '« Tolérance » : choisissez un écart entier de 1 à 6 FCFA.';
  }
  if (tolerance >= unite / 2) {
    return `« Tolérance » : elle doit rester inférieure à la moitié de l'unité d'arrondi (${unite / 2} FCFA).`;
  }
  return null;
};

// ============================================================================
// Petits éléments communs
// ============================================================================

/** Durée lisible : « 45 minutes », « 1 heure », « 1 h 30 ». */
const enClair = (minutes) => {
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (!reste) return `${heures} heure${heures > 1 ? 's' : ''}`;
  return `${heures} h ${String(reste).padStart(2, '0')}`;
};

const BOUTON_REINITIALISER = 'flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
const boutonEnregistrer = (actif, accent) => ({
  className: `flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
    actif ? 'hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
  }`,
  style: actif ? { backgroundColor: accent } : undefined
});

const BOUTON_ECOUTER = 'flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-600 transition-all hover:border-gray-300 hover:bg-gray-50';

/** Choix parmi quelques valeurs : jamais de saisie libre, donc jamais de faute de frappe. */
const ChoixSegmente = ({ options, valeur, onChoisir, accent, desactive, largeur = 'min-w-[56px]', libelle }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="radiogroup" aria-label={libelle}>
    {options.map((option) => {
      const choisi = option === valeur;
      return (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={choisi}
          onClick={() => onChoisir(option)}
          disabled={desactive}
          className={`h-8 ${largeur} rounded-md px-2 text-xs font-semibold transition-all disabled:cursor-not-allowed ${
            choisi ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-white'
          }`}
          style={choisi ? { backgroundColor: accent } : undefined}
        >
          {option}
        </button>
      );
    })}
  </div>
);

const RappelNonEnregistre = ({ onEnregistrer, saving, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
      <AlertCircle size={15} className="text-amber-600" />
    </div>
    <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
    <Button
      onClick={onEnregistrer}
      disabled={saving}
      className="ml-auto h-9 min-w-[168px] justify-center gap-2 text-white hover:opacity-90"
      style={{ backgroundColor: accent }}
    >
      {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
    </Button>
  </div>
);

const ActionsBrouillon = ({ onReinitialiser, onEnregistrer, saving, hasChanges, lectureSeule, accent }) => {
  const actif = hasChanges && !lectureSeule;
  const enregistrer = boutonEnregistrer(actif, accent);
  return (
    <>
      <button
        type="button"
        onClick={onReinitialiser}
        disabled={saving || !hasChanges}
        className={BOUTON_REINITIALISER}
      >
        <RotateCcw size={13} /> Réinitialiser
      </button>
      <button
        type="button"
        onClick={onEnregistrer}
        disabled={saving || !actif}
        className={enregistrer.className}
        style={enregistrer.style}
      >
        {saving
          ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          : <Save size={13} />}
        Enregistrer
      </button>
    </>
  );
};

// ============================================================================
// Onglet Réglages (régime B) : rappel, sons et seuils, une seule barre
// ============================================================================

// Des cas réels ou typiques, pour voir ce que la règle retient avec les
// seuils affichés, avant de les enregistrer.
const EXEMPLES = [
  { valeur: 999, note: 'un cran de molette sous 1 000' },
  { valeur: 1748, note: 'deux crans sous 1 750' },
  { valeur: 1502, note: 'deux crans au-dessus de 1 500' },
  { valeur: 3, note: 'inscription offerte, trois crans au-dessus de 0' },
  { valeur: 2143, note: 'part de famille : 15 000 ÷ 7' },
  { valeur: 1750, note: 'montant rond' }
];

/** La règle du serveur : 1 <= |v - ROUND(v/U)*U| <= T, sur une valeur entière positive. */
const examiner = (valeur, unite, tolerance) => {
  const presume = Math.round(valeur / unite) * unite;
  const ecart = Math.abs(valeur - presume);
  return { signale: valeur > 0 && ecart >= 1 && ecart <= tolerance, presume };
};

/**
 * Le rappel, les sons et les seuils : trois groupes de réglages au même
 * régime B, réunis le 16/10/2026 pour que la page reste à quatre onglets.
 * Un seul brouillon à valider, une seule barre ; chaque groupe garde ses
 * champs, et seuls les champs changés partent. Les seuils changent ce que
 * montre le suivi : leur enregistrement le relit, celui du rappel non.
 */
const SectionReglages = ({
  enregistre, brouillonRappel, setBrouillonRappel, brouillonSeuils, setBrouillonSeuils,
  lectureSeule, ecrire, notifier, signalerEchec
}) => {
  const [saving, setSaving] = useState(false);
  const accent = ACCENT_REGLAGES;

  const rappelChange = rappelModifie(brouillonRappel, enregistre);
  const seuilsChanges = seuilsModifies(brouillonSeuils, enregistre);
  const hasChanges = rappelChange || seuilsChanges;
  const erreurSaisie = erreurIntervalle(brouillonRappel.intervalle);
  const minutes = Number(brouillonRappel.intervalle);

  const resultats = EXEMPLES.map((ex) => ({
    ...ex,
    ...examiner(ex.valeur, brouillonSeuils.arrondi_unite, brouillonSeuils.arrondi_tolerance)
  }));
  const signales = resultats.filter((r) => r.signale).length;

  const modifierRappel = (champ, valeur) => setBrouillonRappel((prec) => ({ ...prec, [champ]: valeur }));
  const modifierSeuils = (champ, valeur) => setBrouillonSeuils((prec) => ({ ...prec, [champ]: valeur }));

  const reinitialiser = () => {
    setBrouillonRappel(versBrouillonRappel(enregistre));
    setBrouillonSeuils(versBrouillonSeuils(enregistre));
  };

  const enregistrer = async () => {
    if (erreurSaisie) {
      signalerEchec(erreurSaisie, 'Valeur à corriger');
      return;
    }
    const erreur = erreurSeuils(brouillonSeuils);
    if (erreur) {
      signalerEchec(erreur, 'Valeur à corriger');
      return;
    }
    setSaving(true);
    // Seuls les champs que l'agent a changés partent : les autres gardent la
    // valeur relue, qu'un autre agent a pu modifier entre-temps.
    const changesRappel = champsChanges(brouillonRappel, versBrouillonRappel(enregistre));
    const changesSeuils = champsChanges(brouillonSeuils, versBrouillonSeuils(enregistre));
    const resultat = await ecrire((base) => ({
      ...base,
      ...('intervalle' in changesRappel ? { intervalle_minutes: Number(changesRappel.intervalle.trim()) } : {}),
      ...('son_active' in changesRappel ? { son_active: changesRappel.son_active } : {}),
      ...('son_notifications' in changesRappel ? { son_notifications: changesRappel.son_notifications } : {}),
      // Mêmes clés que les réglages : seuls les seuils changés recouvrent la base relue.
      ...changesSeuils
    }), { changeSuivi: Object.keys(changesSeuils).length > 0 });
    setSaving(false);
    if (resultat.ok) {
      setBrouillonRappel(versBrouillonRappel(resultat.reglages));
      setBrouillonSeuils(versBrouillonSeuils(resultat.reglages));
      notifier('Réglages enregistrés', 'success');
    } else {
      signalerEchec(resultat.message, 'Enregistrement impossible');
    }
  };

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={SlidersHorizontal}
        titre="Rappel, sons et seuils"
        sousTitre="Quand le panneau revient, ce qu'on entend et la règle de la faute de molette, pour toute l'école."
        accent={accent}
        accentSombre={ACCENT_REGLAGES_SOMBRE}
        actions={
          <ActionsBrouillon
            onReinitialiser={reinitialiser}
            onEnregistrer={enregistrer}
            saving={saving}
            hasChanges={hasChanges}
            lectureSeule={lectureSeule}
            accent={accent}
          />
        }
      />

      <CarteReglages titre="Réouverture du panneau" accent={accent} compteur="1 réglage" dense>
        <LigneReglage
          accent={accent}
          libelle="Intervalle de réouverture"
          description="Le panneau s'ouvre au centre de l'écran à la connexion, puis revient après ce délai tant qu'une anomalie reste à traiter. Il ne revient plus quand tout est réglé."
        >
          <div className="w-36">
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={brouillonRappel.intervalle}
                // La saisie est gardée telle quelle, jamais filtrée ni tronquée :
                // « 1440 » ou « 4,5 » restent visibles et sont refusés avec un
                // message qui nomme le champ, au lieu de partir en 144 ou 45.
                // Changer en silence la valeur tapée, c'est la faute même que
                // ces alertes combattent.
                onChange={(e) => modifierRappel('intervalle', e.target.value)}
                // Gelé pendant l'écriture : la réponse du serveur remplace le
                // brouillon, une frappe faite entre-temps serait perdue.
                disabled={lectureSeule || saving}
                aria-label="Intervalle de réouverture, en minutes"
                aria-invalid={Boolean(erreurSaisie)}
                className={`h-9 w-full rounded-lg border px-3 pr-12 text-sm font-semibold focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-50 ${
                  erreurSaisie
                    ? 'border-red-300 bg-red-50 text-red-900 focus:ring-red-200'
                    : 'border-gray-200 text-gray-800 focus:border-[#246990] focus:ring-[#246990]/15'
                }`}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">min</span>
            </div>
            <p className={`mt-1.5 text-[11px] leading-relaxed ${erreurSaisie ? 'text-red-700' : 'text-gray-400'}`}>
              {erreurSaisie
                ? (/^\d+$/.test(String(brouillonRappel.intervalle || '').trim())
                  ? `Entre ${INTERVALLE_MIN} et ${INTERVALLE_MAX} minutes`
                  : `Nombre entier, de ${INTERVALLE_MIN} à ${INTERVALLE_MAX} minutes`)
                : minutes >= 60 ? `soit ${enClair(minutes)}` : `de ${INTERVALLE_MIN} à ${INTERVALLE_MAX} minutes`}
            </p>
          </div>
        </LigneReglage>
      </CarteReglages>

      <CarteReglages titre="Sons" accent={accent} compteur="2 réglages" dense>
        <LigneReglage
          actif={brouillonRappel.son_active}
          accent={accent}
          libelle="Son du panneau"
          description="Un signal sonore insistant accompagne chaque ouverture du panneau des alertes critiques. Éteint, le panneau s'ouvre en silence."
        >
          <button type="button" onClick={() => jouerSon('alerte')} className={BOUTON_ECOUTER}>
            Écouter
          </button>
          <Interrupteur
            actif={brouillonRappel.son_active}
            accent={accent}
            onBasculer={() => modifierRappel('son_active', !brouillonRappel.son_active)}
            desactive={lectureSeule || saving}
            enCours={saving}
            libelle="Son du panneau"
          />
        </LigneReglage>

        <LigneReglage
          actif={brouillonRappel.son_notifications}
          accent={accent}
          libelle="Son des notifications"
          description="Un signal discret quand une nouvelle notification arrive dans la cloche. Éteint, seul le compteur de la cloche change."
        >
          <button type="button" onClick={() => jouerSon('notification')} className={BOUTON_ECOUTER}>
            Écouter
          </button>
          <Interrupteur
            actif={brouillonRappel.son_notifications}
            accent={accent}
            onBasculer={() => modifierRappel('son_notifications', !brouillonRappel.son_notifications)}
            desactive={lectureSeule || saving}
            enCours={saving}
            libelle="Son des notifications"
          />
        </LigneReglage>
      </CarteReglages>

      <CarteReglages titre="Seuils de détection" accent={accent} compteur="2 réglages" dense>
        <LigneReglage
          accent={accent}
          libelle="Unité d'arrondi"
          description="Les montants de l'école sont des multiples de cette unité. Un montant qui s'en écarte de peu est signalé, avec le multiple le plus proche comme valeur probable."
        >
          <ChoixSegmente
            options={UNITES}
            valeur={brouillonSeuils.arrondi_unite}
            onChoisir={(v) => modifierSeuils('arrondi_unite', v)}
            accent={accent}
            desactive={lectureSeule || saving}
            largeur="min-w-[56px]"
            libelle="Unité d'arrondi"
          />
          <span className="text-xs text-gray-400">FCFA</span>
        </LigneReglage>

        <LigneReglage
          accent={accent}
          libelle="Tolérance"
          description="Écart maximal entre un montant et le multiple le plus proche pour qu'il soit signalé. Un montant exactement sur un multiple ne l'est jamais."
        >
          <ChoixSegmente
            options={TOLERANCES}
            valeur={brouillonSeuils.arrondi_tolerance}
            onChoisir={(v) => modifierSeuils('arrondi_tolerance', v)}
            accent={accent}
            desactive={lectureSeule || saving}
            largeur="w-9"
            libelle="Tolérance"
          />
          <span className="text-xs text-gray-400">FCFA</span>
        </LigneReglage>
      </CarteReglages>

      <CarteReglages
        titre="Exemples"
        accent={accent}
        compteur={`${signales} signalé${signales > 1 ? 's' : ''} sur ${resultats.length}`}
        dense
      >
        {resultats.map((r) => (
          <div key={r.valeur} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div className="flex min-w-[220px] flex-1 items-baseline gap-3">
              <span className="w-24 flex-shrink-0 text-sm font-semibold text-gray-800">{formaterFCFA(r.valeur)}</span>
              <span className="text-[11px] text-gray-400">{r.note}</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Colonne de largeur fixe, alignée à gauche, gardée vide sur une
                  ligne non signalée : les montants présumés s'alignent d'une
                  ligne à l'autre (demande de Daniel du 14/10/2026). */}
              <span className="w-[150px] text-left text-[11px] tabular-nums text-gray-500">
                {r.signale ? `probablement ${formaterFCFA(r.presume)}` : ''}
              </span>
              <span
                className={`inline-flex h-6 w-[96px] items-center justify-center whitespace-nowrap rounded-lg border text-[11px] font-semibold ${
                  r.signale ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-500'
                }`}
              >
                {r.signale ? 'Signalé' : 'Non signalé'}
              </span>
            </div>
          </div>
        ))}
      </CarteReglages>

      <Encadre titre="Pourquoi 50 et 5 ?">
        <p>
          Un cran de molette sur un champ de montant ajoute ou retire 1 franc : 1 000 devient 999
          ou 1 001, 1 500 devient 1 502 au deuxième cran. Les tarifs et les réductions de l'école
          sont en principe des multiples de 50 ; un montant à 5 francs ou moins d'un multiple de 50
          trahit donc presque toujours un cran de trop.
        </p>
        <p className="mt-1">
          Une part de famille divisée par 3, 6 ou 7 tombe à 7 francs ou plus d'un multiple de 50
          (15 000 ÷ 7 = 2 143, à 7 francs de 2 150) : elle n'est jamais signalée. Divisée par 8 ou 9,
          elle peut tomber à 6 francs (16 050 ÷ 8 = 2 006) : une tolérance de 6 la signalerait, d'où
          la valeur de 5 par défaut. Au-delà de 9 parts, l'écart peut tomber à 5 francs ou moins
          (15 050 ÷ 12 = 1 254, à 4 francs de 1 250) : ces cas rares restent signalés, et un
          validateur peut les constater.
        </p>
      </Encadre>

      {hasChanges && !lectureSeule && (
        <RappelNonEnregistre onEnregistrer={enregistrer} saving={saving} accent={accent} />
      )}
    </div>
  );
};

// ============================================================================
// Onglet Détecteurs (régime A)
// ============================================================================

const SectionDetecteurs = ({ detecteurs, enregistre, lectureSeule, ecrire, notifier, signalerEchec }) => {
  // Valeur affichée pendant l'écriture ; vidée ensuite, l'état enregistré fait
  // foi. Un refus du serveur ramène donc l'interrupteur où il était.
  const [optimiste, setOptimiste] = useState({});
  const [enCours, setEnCours] = useState(null);

  const estActif = (id) => (id in optimiste ? optimiste[id] : enregistre.detecteurs[id] !== false);
  const actifs = detecteurs.filter((d) => estActif(d.id)).length;

  const basculer = async (detecteur) => {
    if (lectureSeule || enCours) return;
    const valeur = !estActif(detecteur.id);
    setOptimiste({ [detecteur.id]: valeur });
    setEnCours(detecteur.id);
    const resultat = await ecrire((base) => ({
      ...base,
      detecteurs: { ...base.detecteurs, [detecteur.id]: valeur }
    }), { changeSuivi: true });
    setEnCours(null);
    setOptimiste({});
    if (resultat.ok) {
      notifier('Configuration mise à jour', 'success');
    } else {
      signalerEchec(resultat.message, 'Réglage refusé');
    }
  };

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={Radar}
        titre="Détecteurs d'anomalies"
        sousTitre="Ce que le panneau recherche. Un détecteur éteint n'est plus calculé, pour aucun agent. Les données suspectes se règlent dans leur onglet."
        accent={ACCENT_DETECTEURS}
        accentSombre={ACCENT_DETECTEURS_SOMBRE}
      />

      <CarteReglages
        titre="Détecteurs"
        accent={ACCENT_DETECTEURS}
        compteur={detecteurs.length ? `${actifs} actif${actifs > 1 ? 's' : ''} sur ${detecteurs.length}` : undefined}
        dense
      >
        {detecteurs.length === 0 && (
          <p className="px-5 py-6 text-xs text-gray-400">
            Aucun détecteur n'est ouvert par la licence de l'établissement.
          </p>
        )}
        {detecteurs.map((detecteur) => {
          const actif = estActif(detecteur.id);
          const gravite = GRAVITES[detecteur.gravite] || GRAVITES.importante;
          return (
            <LigneReglage
              key={detecteur.id}
              actif={actif}
              accent={ACCENT_DETECTEURS}
              libelle={detecteur.titre}
              description={detecteur.description}
            >
              <span
                className={`inline-flex h-6 w-[88px] items-center justify-center whitespace-nowrap rounded-lg border text-[11px] font-semibold ${
                  actif ? gravite.classes : 'border-gray-200 bg-gray-50 text-gray-400'
                }`}
              >
                {gravite.libelle}
              </span>
              <Interrupteur
                actif={actif}
                accent={ACCENT_DETECTEURS}
                onBasculer={() => basculer(detecteur)}
                desactive={lectureSeule || Boolean(enCours)}
                enCours={enCours === detecteur.id}
                libelle={detecteur.titre}
              />
            </LigneReglage>
          );
        })}
      </CarteReglages>
    </div>
  );
};

// ============================================================================
// La page
// ============================================================================

const ParametresAlertesCritiques = ({ onBackToDashboard }) => {
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  // Lecture refusée (403) : un droit qui manque, pas une panne à réessayer.
  const [refus, setRefus] = useState(false);
  const [brouillonRappel, setBrouillonRappel] = useState(null);
  const [brouillonSeuils, setBrouillonSeuils] = useState(null);
  const [onglet, setOnglet] = useState('suivi');
  const { notification, notifier, masquer, signalerEchec } = useNotification();
  // Le suivi, lu par la page : il survit au changement d'onglet.
  const suivi = useSuiviAlertes();
  const { charger: chargerSuivi } = suivi;
  // Les données suspectes, lues à la première ouverture de leur onglet.
  const suspectes = useDonneesSuspectes();
  const { charger: chargerSuspectes, lu: suspectesLues } = suspectes;

  // Derniers réglages connus du serveur, et liste des détecteurs : relus et
  // lus au moment où une écriture part, pas au moment où elle a été demandée.
  const enregistreRef = useRef(null);
  const detecteursRef = useRef([]);
  const fileEcritures = useRef(Promise.resolve());
  // Un réglage qui change le suivi (détecteur, signal, seuils, vérification)
  // a été enregistré pendant qu'un autre onglet était affiché : le suivi se
  // relit à son retour.
  const suiviPerime = useRef(false);
  // L'onglet affiché, lu à la fin d'une écriture (l'état serait celui du
  // moment où elle est partie), et si l'agent en a déjà choisi un.
  const ongletRef = useRef('suivi');
  const ongletChoisi = useRef(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const reponse = await lireReglagesAlertes();
      if (reponse?.success === false) throw new Error(reponse.message || 'Lecture des réglages impossible');
      if (!reponseComplete(reponse)) throw new Error('Réponse du serveur incomplète.');
      const reglages = normaliser(reponse.reglages);
      const detecteurs = reponse.detecteurs;
      const droits = { modifier: Boolean(reponse.droits?.modifier) };
      enregistreRef.current = reglages;
      detecteursRef.current = detecteurs;
      // Le suivi est gardé par la même porte que `droits.modifier` : sans
      // elle, la page s'ouvre sur les Réglages plutôt que sur un refus, sauf
      // si l'agent a déjà choisi son onglet. Posé avant l'état, pour que les
      // onglets apparaissent directement sur le bon.
      if (!droits.modifier && !ongletChoisi.current) {
        ongletRef.current = 'reglages';
        setOnglet('reglages');
      }
      setEtat({
        reglages,
        detecteurs,
        migrationManquante: Boolean(reponse.migrationManquante),
        anneeCloturee: anneeEnLectureSeule(),
        droits
      });
      setBrouillonRappel(versBrouillonRappel(reglages));
      setBrouillonSeuils(versBrouillonSeuils(reglages));
      setErreur(null);
      setRefus(false);
    } catch (e) {
      if (e?.statut === 403) {
        setRefus(true);
        setErreur(null);
      } else {
        setRefus(false);
        setErreur(e?.message || 'Lecture des réglages impossible');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Prend `reglages` (et la liste des détecteurs, si elle est fournie) pour
   * état enregistré. Les champs de brouillon que l'agent n'a pas touchés
   * suivent la nouvelle valeur ; les autres restent tels qu'il les a saisis.
   */
  const adopter = useCallback((reglages, detecteurs) => {
    const avant = enregistreRef.current;
    enregistreRef.current = reglages;
    if (detecteurs) detecteursRef.current = detecteurs;
    setEtat((prec) => (prec ? { ...prec, reglages, ...(detecteurs ? { detecteurs } : {}) } : prec));
    if (avant) {
      setBrouillonRappel((prec) => rebaser(prec, versBrouillonRappel(avant), versBrouillonRappel(reglages)));
      setBrouillonSeuils((prec) => rebaser(prec, versBrouillonSeuils(avant), versBrouillonSeuils(reglages)));
    }
  }, []);

  /**
   * Enregistre `modifier(réglages relus)`, après les écritures déjà en cours.
   * `changeSuivi` : le réglage change ce que montre le suivi (détecteurs,
   * signaux, seuils), qui se relit alors. Rend { ok, reglages } ou { ok: false, message }.
   */
  const ecrire = useCallback((modifier, { changeSuivi = false } = {}) => {
    const tache = fileEcritures.current.then(async () => {
      // Relire avant de composer l'envoi : l'objet part COMPLET, et une base
      // périmée remettrait à l'ancienne valeur ce qu'un autre agent, ou un
      // autre onglet, vient de changer. Relecture en échec : base connue.
      try {
        const frais = await lireReglagesAlertes();
        if (frais?.success !== false && reponseComplete(frais)) {
          adopter(normaliser(frais.reglages), frais.detecteurs);
        }
      } catch {
        // Le PUT dira lui-même si le serveur refuse.
      }

      const envoi = completer(modifier(enregistreRef.current), detecteursRef.current);
      try {
        const reponse = await enregistrerReglagesAlertes(envoi);
        if (reponse?.success === false) {
          return { ok: false, message: reponse.message || "Erreur lors de l'enregistrement" };
        }
        // Un champ absent de la réponse, détecteurs compris, garde la valeur
        // envoyée : jamais une valeur par défaut affichée comme enregistrée.
        const acceptes = normaliser(reponse?.reglages, envoi);
        adopter(acceptes);
        memoriserReglagesSon(acceptes);
        if (changeSuivi) {
          // Lu à la réponse, pas à l'envoi : l'agent a pu passer sur le Suivi
          // pendant l'écriture, qui se relit alors sous ses yeux.
          if (ongletRef.current === 'suivi') chargerSuivi();
          else suiviPerime.current = true;
        }
        return { ok: true, reglages: acceptes };
      } catch (e) {
        // La table manque depuis la lecture : la page passe en lecture seule.
        if (e?.code === 'MIGRATION_MANQUANTE') {
          setEtat((prec) => (prec ? { ...prec, migrationManquante: true } : prec));
        }
        // Année clôturée que le drapeau de connexion ignorait : lecture seule
        // aussi, avec un message accentué à la place de celui du verrou.
        if (refusAnneeCloturee(e)) {
          setEtat((prec) => (prec ? { ...prec, anneeCloturee: true } : prec));
          return { ok: false, message: MESSAGE_ANNEE_CLOTUREE };
        }
        return { ok: false, message: e?.message || "Erreur lors de l'enregistrement" };
      }
    });
    fileEcritures.current = tache.catch(() => {});
    return tache;
  }, [adopter, chargerSuivi]);

  const lectureSeule = useMemo(
    () => Boolean(etat?.migrationManquante) || Boolean(etat?.anneeCloturee) || !etat?.droits?.modifier,
    [etat]
  );

  const marqueReglages = etat && !lectureSeule
    && (rappelModifie(brouillonRappel, etat.reglages) || seuilsModifies(brouillonSeuils, etat.reglages));

  const propsSection = { lectureSeule, ecrire, notifier, signalerEchec };

  // Les données suspectes se lisent à la première ouverture de leur onglet,
  // pour un agent qui a la porte (sinon l'onglet le dit, sans rien lire).
  const ouvrirSuspectes = useCallback(() => {
    if (!suspectesLues && etat?.droits?.modifier) chargerSuspectes();
  }, [suspectesLues, etat, chargerSuspectes]);

  // Retour sur le suivi après un enregistrement qui le change : il se relit.
  const changerOnglet = useCallback((valeur) => {
    ongletChoisi.current = true;
    ongletRef.current = valeur;
    setOnglet(valeur);
    if (valeur === 'suivi' && suiviPerime.current) {
      suiviPerime.current = false;
      chargerSuivi();
    }
    if (valeur === 'suspectes') ouvrirSuspectes();
  }, [chargerSuivi, ouvrirSuspectes]);

  const surReglages = onglet === 'reglages' || onglet === 'detecteurs';
  // L'onglet des données suspectes a ses interrupteurs : il prend les bandeaux
  // de la migration et de l'année clôturée ; sans la porte, il dit lui-même
  // son refus.
  const surSuspectesAutorise = onglet === 'suspectes' && Boolean(etat?.droits?.modifier);

  // La panne qui a fait échouer les réglages a souvent fait échouer le suivi :
  // « Réessayer » relit les deux, pour ne pas laisser un second bandeau derrière.
  const reessayer = () => {
    charger();
    if (suivi.erreur) chargerSuivi();
  };

  return (
    <div className="w-full">
      <PorteurNotification notification={notification} onFermer={masquer} />

      <EnTeteParametrage
        onRetour={onBackToDashboard}
        titre="Paramètres des alertes critiques"
        sousTitre="Suivi des alertes, données suspectes, rappel et sons du panneau, seuils de détection des montants, détecteurs d'anomalies"
      />

      {loading && !etat && (
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          {/* À l'accent du premier onglet, le Suivi, qui s'ouvre ensuite pour
              les agents habilités (les autres arrivent sur les Réglages). */}
          <ChargementSection accent={ACCENT_SUIVI} libelle="Chargement des alertes critiques…" />
        </div>
      )}

      {!loading && erreur && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0 text-sm text-red-800">
            <p className="font-semibold text-red-900">Les réglages n'ont pas pu être lus</p>
            <p className="mt-1">{erreur}</p>
            {/* Classes explicites, les mêmes que le « Réessayer » du Suivi : la
                variante outline de ui/button sort transparente (constat du 13/10/2026). */}
            <button type="button" onClick={reessayer} className={BOUTON_REESSAYER}>
              <RefreshCw size={15} /> Réessayer
            </button>
          </div>
        </div>
      )}

      {!loading && refus && (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
          <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />
          <p>Ces réglages sont réservés aux agents habilités à gérer les alertes critiques.</p>
        </div>
      )}

      {etat && !erreur && !refus && brouillonRappel && brouillonSeuils && (
        <>
          {(surReglages || surSuspectesAutorise) && etat.migrationManquante && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <p>
                Cette école n'a pas encore reçu la mise à jour des alertes critiques : les réglages par
                défaut s'appliquent (réouverture toutes les 30 minutes, sons activés, tous les détecteurs
                d'anomalies, données suspectes éteintes, unité de 50 et tolérance de 5 FCFA) et ne peuvent
                pas être changés. Contactez l'équipe technique.
              </p>
            </div>
          )}

          {surReglages && !etat.migrationManquante && !etat.droits.modifier && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
              <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <p>
                Vous consultez ces réglages sans pouvoir les modifier : leur modification est réservée aux
                agents habilités à gérer les alertes critiques.
              </p>
            </div>
          )}

          {/* Après le droit : sans droit, l'année n'y changerait rien. */}
          {(surReglages || surSuspectesAutorise) && !etat.migrationManquante && etat.droits.modifier && etat.anneeCloturee && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
              <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />
              <p>{MESSAGE_ANNEE_CLOTUREE}</p>
            </div>
          )}

          <Tabs value={onglet} onValueChange={changerOnglet} className="w-full">
            <TabsList className="mb-6 grid h-auto grid-cols-2 gap-1 rounded-xl bg-[#246990]/8 p-1 sm:grid-cols-4">
              <TabsTrigger value="suivi" className={`${ONGLET} data-[state=active]:bg-[#7c3aed]`}>
                <Activity size={15} className="mr-2" />
                <span>Suivi</span>
              </TabsTrigger>
              <TabsTrigger value="suspectes" className={`${ONGLET} data-[state=active]:bg-[#a14a76]`}>
                <FileSearch size={15} className="mr-2" />
                <span>Données suspectes</span>
              </TabsTrigger>
              <TabsTrigger value="reglages" className={`${ONGLET} data-[state=active]:bg-[#246990]`}>
                <SlidersHorizontal size={15} className="mr-2" />
                <span>Réglages</span>
                {marqueReglages && (
                  <>
                    <span className="ml-2 h-1.5 w-1.5 rounded-full bg-amber-400" aria-hidden="true" />
                    <span className="sr-only">(modifications non enregistrées)</span>
                  </>
                )}
              </TabsTrigger>
              <TabsTrigger value="detecteurs" className={`${ONGLET} data-[state=active]:bg-[#319190]`}>
                <Radar size={15} className="mr-2" />
                <span>Détecteurs</span>
              </TabsTrigger>
            </TabsList>

            <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
              <TabsContent value="suivi" className="mt-0 focus-visible:outline-none">
                <SuiviAlertesCritiques {...suivi} />
              </TabsContent>
              <TabsContent value="suspectes" className="mt-0 focus-visible:outline-none">
                <DonneesSuspectes
                  {...suspectes}
                  sansDroit={!etat.droits.modifier}
                  reglages={etat.reglages}
                  anneeCloturee={Boolean(etat.anneeCloturee)}
                  onVerification={() => { suiviPerime.current = true; }}
                  {...propsSection}
                />
              </TabsContent>
              <TabsContent value="reglages" className="mt-0 focus-visible:outline-none">
                <SectionReglages
                  enregistre={etat.reglages}
                  brouillonRappel={brouillonRappel}
                  setBrouillonRappel={setBrouillonRappel}
                  brouillonSeuils={brouillonSeuils}
                  setBrouillonSeuils={setBrouillonSeuils}
                  {...propsSection}
                />
              </TabsContent>
              <TabsContent value="detecteurs" className="mt-0 focus-visible:outline-none">
                <SectionDetecteurs
                  detecteurs={etat.detecteurs.filter((d) => d.categorie !== CATEGORIE_SUSPECTES)}
                  enregistre={etat.reglages}
                  {...propsSection}
                />
              </TabsContent>
            </div>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default ParametresAlertesCritiques;
